/**
 * Active "operate as client" support-session state for the Relay browser client.
 *
 * Two consumers need this outside of React:
 *   1. The low-level Supabase `fetch` wrapper (lib/supabase.ts) attaches the
 *      `x-support-session-id` header on every request by reading the active
 *      session id here — without importing React context into every data file.
 *   2. The AgencySessionContext mirrors this into React state for the UI.
 *
 * Persistence: the session is stored in `sessionStorage` so it survives a full
 * page reload (manual refresh, an OAuth/integration redirect return, or the
 * ErrorBoundary's reload). Previously this was process-local only, so any
 * reload silently dropped the operate-as session — the header source reset to
 * null and the app fell back to the user's base org. sessionStorage is
 * per-tab and cleared when the tab closes, which matches operate-as semantics
 * (the backend session is short-lived and tab-scoped); it deliberately does
 * NOT use localStorage, which would leak the session across unrelated tabs. The
 * record also stores the owning Supabase auth user id so a session from one
 * signed-in user is never rehydrated into another user in the same tab. It also
 * stores the server-side support-session expiry and refuses to rehydrate stale
 * sessions, preventing the request wrapper from sending expired headers after a
 * long-lived tab refresh.
 *
 * The module-level cache is seeded synchronously at import time so the very
 * first Supabase request after a reload already carries the header, before
 * React has mounted.
 */
export interface ActiveAgencySession {
  /**
   * Supabase auth user that started/owns this session. Persisted so a session
   * left in a tab's sessionStorage is never rehydrated for a DIFFERENT user
   * who later signs in in the same tab — otherwise the new user would inherit
   * the previous user's operated-client banner/context (the backend rejects
   * the stale header, but the UI would still show a denied client context
   * until manually exited).
   */
  authUserId: string;
  sessionId: string;
  clientOrgId: string;
  clientOrgName: string;
  clientOrgSlug: string;
  /** ISO timestamp for when the backend support session should expire. */
  expiresAt: string;
}

const STORAGE_KEY = "relay-agency-session";
// Keep in sync with public.relay_agency_start_client_session's default p_expires_in.
export const DEFAULT_AGENCY_SESSION_TTL_MS = 8 * 60 * 60 * 1000;

export function getDefaultAgencySessionExpiresAt(): string {
  return new Date(Date.now() + DEFAULT_AGENCY_SESSION_TTL_MS).toISOString();
}

function isExpired(expiresAt: string | undefined): boolean {
  if (!expiresAt) return true;
  const expiresAtMs = Date.parse(expiresAt);
  return Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now();
}

function clearStorage() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Storage can be blocked; nothing to clear. */
  }
}

function readFromStorage(): ActiveAgencySession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveAgencySession>;
    // Require the owning user id and expiry — entries without them (incl. any
    // pre-upgrade format) are treated as unusable rather than rehydrated.
    if (!parsed || !parsed.sessionId || !parsed.clientOrgId || !parsed.authUserId || isExpired(parsed.expiresAt)) {
      clearStorage();
      return null;
    }
    return {
      authUserId: parsed.authUserId,
      sessionId: parsed.sessionId,
      clientOrgId: parsed.clientOrgId,
      clientOrgName: parsed.clientOrgName ?? "",
      clientOrgSlug: parsed.clientOrgSlug ?? "",
      expiresAt: parsed.expiresAt!,
    };
  } catch {
    return null;
  }
}

let active: ActiveAgencySession | null = readFromStorage();

/** Full active session (sessionId + operated-client identity), or null. */
export function getActiveAgencySession(): ActiveAgencySession | null {
  if (active && isExpired(active.expiresAt)) {
    active = null;
    clearStorage();
  }
  return active;
}

/** Just the active support-session id — used by the Supabase fetch wrapper. */
export function getActiveAgencySessionId(): string | null {
  return getActiveAgencySession()?.sessionId ?? null;
}

type ActiveAgencySessionUpdate =
  | ActiveAgencySession
  | null
  | ((current: ActiveAgencySession | null) => ActiveAgencySession | null);

/** Set or clear the active session, mirroring the change to sessionStorage. */
export function setActiveAgencySession(session: ActiveAgencySessionUpdate) {
  const current = getActiveAgencySession();
  const next = typeof session === "function" ? session(current) : session;
  active = next && !isExpired(next.expiresAt) ? next : null;
  try {
    if (active) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(active));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* Storage can be blocked; keep the in-memory value. */
  }
}
